import { useCallback, useEffect, useState } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";
import {
  clearGoogleHealthcareAccessToken,
  GOOGLE_HEALTHCARE_SCOPE_STRING,
} from "@/services/google/health";

declare global {
  interface Window {
    tokenClientHealthcare: any;
    googlePopupHealthcareTimeout: NodeJS.Timeout;
  }
}

interface UseGoogleHealthcareAuthReturn {
  isConnected: boolean;
  isLoading: boolean;
  accessToken: string | null;
  handleLogin: () => void;
  handleLogout: () => Promise<void>;
}

export function useGoogleHealthcareAuth(): UseGoogleHealthcareAuthReturn {
  const { user, fetchUser } = useSessionContext();
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem("googleHealthcareAccessToken")
  );
  const googleHealthcareClientId = import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const googleHealthcareClientSecret = import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET || import.meta.env.VITE_GOOGLE_CLIENT_SECRET;
  const googleRedirectUri = window.location.origin;
  const isConnected = Boolean(user?.refreshTokenGoogleFit);

  const handleTokenResponse = useCallback(
    async (response: any) => {
      try {
        if (!response?.code) return;

        const tokenResponse = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: googleHealthcareClientId,
            client_secret: googleHealthcareClientSecret,
            code: response.code,
            grant_type: "authorization_code",
            access_type: "offline",
            redirect_uri: googleRedirectUri,
            scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
          }),
        });

        if (!tokenResponse.ok) {
          throw new Error("Falha ao obter tokens Google Health");
        }

        const tokens = await tokenResponse.json();
        if (!tokens.refresh_token) {
          throw new Error("Refresh token Google Health não recebido");
        }

        await DeviceIDPatch({
          idUser: user?.id,
          operation: "replace",
          pathName:
            "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
          valueField: tokens.refresh_token,
        });

        localStorage.setItem("googleHealthcareAccessToken", tokens.access_token);
        // Sinaliza para o dashboard reabrir o modal após vínculo bem-sucedido.
        localStorage.setItem("reopenFitnessModalAfterLink", "true");
        setAccessToken(tokens.access_token);
        await fetchUser();
      } catch (error) {
        console.error("Erro ao autenticar Google Health:", error);
        alert("Não foi possível conectar ao Google Health.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      fetchUser,
      googleHealthcareClientId,
      googleHealthcareClientSecret,
      googleRedirectUri,
      user?.id,
    ]
  );

  const initializeGoogleClient = useCallback(() => {
    if (!window.google) return;

    window.tokenClientHealthcare = window.google.accounts.oauth2.initCodeClient({
      client_id: googleHealthcareClientId,
      scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
      callback: (response: any) => {
        if (window.googlePopupHealthcareTimeout) {
          clearTimeout(window.googlePopupHealthcareTimeout);
        }
        handleTokenResponse(response);
      },
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: false,
      select_account: false,
      ux_mode: "popup",
      redirect_uri: googleRedirectUri,
    });
  }, [googleHealthcareClientId, googleRedirectUri, handleTokenResponse]);

  useEffect(() => {
    const gsiScript = document.createElement("script");
    gsiScript.src = "https://accounts.google.com/gsi/client";
    gsiScript.async = true;
    gsiScript.defer = true;
    gsiScript.onload = initializeGoogleClient;
    document.head.appendChild(gsiScript);

    const handleGoogleMessage = (event: MessageEvent) => {
      if (
        event.origin.includes("accounts.google.com") &&
        event.data &&
        (event.data.type === "cancel" || event.data.type === "close")
      ) {
        setIsLoading(false);
      }
    };

    window.addEventListener("message", handleGoogleMessage);

    return () => {
      window.removeEventListener("message", handleGoogleMessage);
      gsiScript.remove();
    };
  }, [initializeGoogleClient]);

  const handleLogin = useCallback(() => {
    if (!window.tokenClientHealthcare) return;

    setIsLoading(true);
    window.googlePopupHealthcareTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 60000);
    window.tokenClientHealthcare.requestCode();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
        valueField: "",
      });
      clearGoogleHealthcareAccessToken();
      setAccessToken(null);
      await fetchUser();
    } catch (error) {
      console.error("Erro ao desconectar Google Health:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser, user?.id]);

  return {
    isConnected,
    isLoading,
    accessToken,
    handleLogin,
    handleLogout,
  };
}
